import { acceptHMRUpdate, defineStore } from 'pinia';
import { useRoute, useRouter } from 'nuxt/app';
import type { LocationQuery, RouteQueryAndHash } from 'vue-router';
import {
  QUERY_KEY_NAME_PAGE,
  QUERY_KEY_NAME_SORT,
  QUERY_KEY_NAME_SORT_DIR,
  PRODUCT_LIST_VIEW_KEY
} from './constants';
import type {
  ProductFiltersModel,
  ReadonlyProductFiltersModel,
  ResponseModel,
  SortDirection
} from './model';
import { createFiltersFromQuery } from './helper/createFiltersFromQuery';
import { CATEGORY_ID } from '~/utils/shop/constants';
import { withReset } from '~/utils/pinia/withReset';
import type { ProductsQueryModel } from '~/server/api/magento/products.get';
import { isDefined } from '~/utils/guards/isDefined';
import { ensureArray } from '~/utils/ensureArray';
import type {
  CommonProductDetailFragment,
  CommonProductFragment,
  ProductAggregationFragment
} from '~/lib/Shop/generated/schema';
import { CurrencyEnum } from '~/lib/Shop/generated/schema';
import { mergeFilters } from '~/utils/shop/mergeFilters';
import { DEFAULT_FETCH_OPTIONS } from '~/utils/defaultFetchOptions';
import type { IProductList } from '~/lib/ContentfulService';

const EXCLUDED_KEYS = [
  QUERY_KEY_NAME_SORT,
  QUERY_KEY_NAME_SORT_DIR,
  QUERY_KEY_NAME_PAGE
];

const SORT_DIRECTION_MAP: Record<string, SortDirection> = {
  asc: 'asc',
  desc: 'desc'
};

const DEFAULT_FILTERS: ReadonlyProductFiltersModel = Object.freeze({
  [QUERY_KEY_NAME_SORT_DIR]: Object.freeze(['asc']),
  [QUERY_KEY_NAME_PAGE]: Object.freeze(['1'])
});

export const PRODUCT_FILTER_STORE_INJECT_KEY = Symbol('product-filter-store');

type ProductFilterStoreDefinition = ReturnType<typeof createProductFilterStore>;

export const useProductFilterStore = (): ProductFilterStoreDefinition => {
  const store = inject<ProductFilterStoreDefinition>(
    PRODUCT_FILTER_STORE_INJECT_KEY
  );
  if (!store) {
    throw new Error('no product filter store provided');
  }

  return store;
};

export function createProductFilterStore(id: string) {
  const store = defineStore(`product-filters-${id}`, () => {
    const route = useRoute();

    const filters = ref<ProductFiltersModel>(
      mergeFilters(DEFAULT_FILTERS, createFiltersFromQuery(route.query))
    );

    const staticFilters = ref<ProductFiltersModel>({});
    const parentSectionId = ref<string | null>(null);
    const lastResponse = ref<ResponseModel | null>(null);
    const loading = ref<boolean>(false);
    const error = ref<unknown>(null);
    const abortController = ref<AbortController | null>(null);
    const lastParams = ref<ProductsQueryModel | null>(null);
    const view = ref<IProductList['fields']['view'] | null>(null);
    const currentPage = ref<number>(1);
    const selectedSortOption = ref<string | undefined>(undefined);

    const _state = {
      filters,
      staticFilters,
      parentSectionId,
      lastResponse,
      loading,
      error,
      abortController,
      lastParams,
      view,
      currentPage,
      selectedSortOption
    };

    const aggregations = computed<ProductAggregationFragment[]>(
      () => lastResponse.value?.aggregations?.filter(isDefined) ?? []
    );

    const products = computed<
      ReadonlyArray<CommonProductFragment | CommonProductDetailFragment>
    >(() => lastResponse.value?.items?.filter(isDefined) ?? []);

    const hasResults = computed(() => {
      return products.value.length > 0;
    });

    const currencyCode = computed(() => {
      return (
        products.value?.[0]?.price_range.minimum_price?.final_price?.currency ??
        CurrencyEnum.Eur
      );
    });

    const activeFilterCount = computed<number>(() => {
      return Object.entries(filters.value).filter(
        ([key, values]) =>
          Array.isArray(values) && values.length && !EXCLUDED_KEYS.includes(key)
      ).length;
    });

    const currentSort = computed(() => {
      return ensureArray(filters.value[QUERY_KEY_NAME_SORT])[0];
    });

    const totalPages = computed(
      () => lastResponse.value?.page_info?.total_pages
    );
    const pageSize = computed(() => lastResponse.value?.page_info?.page_size);
    const totalResultCount = computed(() => lastResponse.value?.total_count);
    const pageCount = computed(
      () => lastResponse.value?.page_info?.total_pages
    );
    const sortOptions = computed(
      () => lastResponse.value?.sort_fields?.options?.filter(isDefined) ?? []
    );
    const defaultSortOption = computed(
      () => lastResponse.value?.sort_fields?.default
    );
    const defaultSortDirection = computed<SortDirection>(() => 'asc');
    const sortDirection = computed<SortDirection>(() => {
      return (
        SORT_DIRECTION_MAP[
          filters.value[QUERY_KEY_NAME_SORT_DIR]?.[0]?.toLowerCase()
        ] ?? defaultSortDirection.value
      );
    });
    const visibleFilters = computed(() => {
      return Object.fromEntries(
        Object.entries(filters.value).filter(
          ([key]) => !EXCLUDED_KEYS.includes(key)
        )
      );
    });

    async function getProducts() {
      const { $shopStore, $locale } = useNuxtApp();

      const query: ProductsQueryModel = {
        store: $shopStore.value!,
        locale: $locale.value!,
        filters: mergeFilters(
          DEFAULT_FILTERS,
          filters.value,
          staticFilters.value,
          {
            [QUERY_KEY_NAME_PAGE]: ensureArray(String(currentPage.value))
          }
        )
      };

      if (JSON.stringify(query) === JSON.stringify(lastParams.value)) {
        return;
      }

      lastParams.value = query;

      if (
        abortController.value &&
        !abortController.value.signal.aborted &&
        loading.value
      ) {
        abortController.value.abort();
      }

      const currentAbortController = new AbortController();
      abortController.value = currentAbortController;

      loading.value = true;
      error.value = null;
      try {
        const productsResponse = await $fetch<ResponseModel>(
          '/api/magento/products',
          {
            ...DEFAULT_FETCH_OPTIONS,
            query,
            signal: currentAbortController.signal
          }
        );

        // remove category uid filter if category id filter is present.
        // @see https://gcp.baslerweb.com/jira/browse/WEB2-2004
        const categoryId = productsResponse?.aggregations?.find(
          (a) => a?.attribute_code === CATEGORY_ID
        );
        const categoryUid = productsResponse?.aggregations?.find(
          (a) => a?.attribute_code === 'category_uid'
        );

        if (productsResponse?.aggregations && categoryId && categoryUid) {
          productsResponse.aggregations = productsResponse.aggregations.filter(
            (a) => a?.attribute_code !== 'category_uid'
          );
        }

        lastResponse.value = productsResponse;
        // we do not set loading false in finally
        // as an aborted request might cancel the animation
        loading.value = false;

        // set default sort if current sort is still empty - e.g. in initial
        // request
        if (!currentSort.value && productsResponse?.sort_fields?.default) {
          filters.value[QUERY_KEY_NAME_SORT] = [
            productsResponse.sort_fields.default
          ];
        }

        // if no products are available for the current page, set page to 1
        if (
          currentPage.value > 1 &&
          isDefined(pageSize.value) &&
          isDefined(totalResultCount.value)
        ) {
          const from = (currentPage.value - 1) * pageSize.value + 1;

          if (from > totalResultCount.value) {
            changePage(1);
          }
        }

        selectedSortOption.value = `${currentSort.value}__${sortDirection.value}`;
      } catch (e) {
        // ignore abort exceptions
        if (currentAbortController.signal.aborted) {
          return;
        }
        error.value = e;
        loading.value = false;
      }
    }

    function resetFilters() {
      filters.value = {};
      currentPage.value = 1;
      pushAnalyticsEventOnFilterReset();
      updateRoute(filters.value);
    }

    function toggleSortDirection(direction?: SortDirection) {
      if (direction) {
        handleFormValueChange(QUERY_KEY_NAME_SORT_DIR, direction);
      } else {
        handleFormValueChange(
          QUERY_KEY_NAME_SORT_DIR,
          sortDirection.value === 'asc' ? 'desc' : 'asc'
        );
      }
    }

    function changeSort(sort: string) {
      handleFormValueChange(QUERY_KEY_NAME_SORT, sort);
    }

    function changePage(page: number) {
      if (totalPages.value != null && page >= 1 && page <= totalPages.value) {
        currentPage.value = page;
        updateRoute(filters.value);
      }
    }

    function changeView(newView: NonNullable<IProductList['fields']['view']>) {
      view.value = newView;
    }

    /**
     * Updates the given filter key with the new value.
     * This will automatically push the filters to the url
     * and fetches products based on the new filters.
     *
     * @param key The key to update
     * @param value The new value.
     */
    function handleFormValueChange(key: string, value: string | string[]) {
      filters.value = {
        ...filters.value,
        [key]: ensureArray(value)
      };
      pushAnalyticsEventOnFilterChange(key, value);

      updateRoute(filters.value);
    }

    function createFiltersFromRoute(query: LocationQuery) {
      filters.value = createFiltersFromQuery(query);
    }

    function createFiltersFromRouteAndUpdate(query: LocationQuery) {
      createFiltersFromRoute(query);

      return getProducts();
    }

    /**
     * Updates the route based on the given filters.
     * This does not check if the current route already has
     * the given filters.
     *
     * @param filters The filters to use.
     */
    function updateRoute(filters: ProductFiltersModel) {
      const router = useRouter();

      router.push(createUrlParams(filters));
    }

    /**
     * Creates relevant url parameters based on the given filters for usage
     * in e.g. {@link useRouter} push method.
     * Default filters are omitted.
     *
     * @param filters The filters to use. Uses current filters by default.
     */
    function createUrlParams(
      newFilters?: ProductFiltersModel
    ): RouteQueryAndHash {
      const clonedFilters = _cloneFilters(newFilters ?? filters.value);

      if (currentSort.value === defaultSortOption.value) {
        delete clonedFilters[QUERY_KEY_NAME_SORT];
      }

      if (sortDirection.value === defaultSortDirection.value) {
        delete clonedFilters[QUERY_KEY_NAME_SORT_DIR];
      }

      if (currentPage.value === 1) {
        delete clonedFilters[QUERY_KEY_NAME_PAGE];
      }

      return {
        query: clonedFilters,
        hash: `#${parentSectionId.value}`
      };
    }

    /**
     * Creates url params (using {@link createUrlParams}) while removing
     * the given value from the given filter key.
     *
     * @param key The filter key to use.
     * @param valueToRemove The value to remove from this key
     */
    function createNewUrlParamsFromFilter(
      key: string,
      valueToRemove: ReadonlyArray<string>
    ) {
      const clonedFilters = _cloneFilters();
      const values = filters.value[key];
      const arrayValues = ensureArray(values).filter(isDefined).map(String);

      clonedFilters[key] = arrayValues.filter(
        (value) => value && !valueToRemove.includes(value)
      );

      return createUrlParams(clonedFilters);
    }

    function pushAnalyticsEventOnFilterReset() {
      const { $analytics } = useNuxtApp();

      $analytics.pushToDataLayer({
        action: 'reset_filter_selection',
        category: 'filter_selection',
        event: 'click_Internal',
        label: 'Reset'
      });
    }

    function pushAnalyticsEventOnFilterChange(
      key: string,
      value: string | string[]
    ) {
      const { $analytics } = useNuxtApp();

      const label = lastResponse.value?.aggregations?.find(
        (aggregation) => aggregation?.attribute_code === key
      )?.label;

      $analytics.pushToDataLayer({
        event: 'click_Internal',
        category: 'filter_selection',
        action: label,
        label: ensureArray(value).join(',')
      });
    }

    /**
     * Creates a (deep) copy of the given filters or current filters.
     *
     * @param newFilters The filters to use. Uses current filters by default.
     * @internal
     */
    function _cloneFilters(
      newFilters?: ProductFiltersModel
    ): ProductFiltersModel {
      return JSON.parse(JSON.stringify(newFilters ?? filters.value));
    }

    function hydrate() {
      view.value = window.localStorage.getItem(PRODUCT_LIST_VIEW_KEY) as
        | 'grid'
        | 'table-list'
        | null;
    }

    return withReset({
      _state,
      filters,
      staticFilters,
      parentSectionId,
      lastResponse,
      loading,
      error,
      abortController,
      lastParams,
      view,
      currentPage,
      selectedSortOption,
      products,
      aggregations,
      hasResults,
      currencyCode,
      activeFilterCount,
      currentSort,
      totalPages,
      pageSize,
      totalResultCount,
      pageCount,
      sortOptions,
      defaultSortOption,
      defaultSortDirection,
      sortDirection,
      visibleFilters,
      handleFormValueChange,
      getProducts,
      resetFilters,
      toggleSortDirection,
      changeSort,
      changePage,
      changeView,
      createFiltersFromRoute,
      createFiltersFromRouteAndUpdate,
      createNewUrlParamsFromFilter,
      pushAnalyticsEventOnFilterReset,
      pushAnalyticsEventOnFilterChange,
      hydrate
    });
  });

  if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(store, import.meta.hot));
  }

  return store();
}
